import axios from "@/plugins/axios";
import utils from "@/stores/utils";

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const STORAGE_KEY = getStorageKey("campaigns", "list");
const FIELDS_TO_SAVE = ["limit", "ordering", "status", "archived"];

const DEFAULT_FILTERS = {
    offset: 0,
    limit: 20,
    ordering: "-creation_date",
    archived: false,
    kind: "campaign",
};

const state = {
    campaigns: [],
    campaigns_count: 0,
    campaigns_filters: { ...DEFAULT_FILTERS, ...loadFiltersFromStorage(STORAGE_KEY, {}) },
    campaigns_loaded: false,
    campaigns_loading: false,
    campaigns_loading_errors: null,
    campaigns_cancel_source: null,

    campaign_saving: false,
    campaign_saving_errors: {},

    /** Sender identity, quota, templates and variables: everything the composer needs. */
    settings: null,
    settings_loading: false,
};

const mutations = {
    updateCampaigns(state, campaigns) {
        state.campaigns = campaigns;
        state.campaigns_loaded = true;
        state.campaigns_loading_errors = null;
    },
    updateCampaignsCount(state, count) {
        state.campaigns_count = count;
    },
    updateCampaignsLoading(state, loading) {
        state.campaigns_loading = loading;
    },
    updateCampaignsLoadingErrors(state, errors) {
        state.campaigns_loading_errors = errors;
        state.campaigns_loading = false;
    },
    updateCampaignsFilters(state, filters) {
        state.campaigns_filters = filters;
        saveFiltersToStorage(STORAGE_KEY, filters, FIELDS_TO_SAVE);
    },
    updateCampaignsCancelSource(state, source) {
        state.campaigns_cancel_source = source;
    },
    updateCampaignSaving(state, saving) {
        state.campaign_saving = saving;
    },
    updateCampaignSavingErrors(state, errors) {
        state.campaign_saving_errors = errors || {};
    },
    updateSettings(state, settings) {
        state.settings = settings;
    },
    updateSettingsLoading(state, loading) {
        state.settings_loading = loading;
    },
};

const actions = {
    async fetchCampaigns({ commit, state }) {
        if (state.campaigns_cancel_source) {
            state.campaigns_cancel_source.cancel("New fetch started");
        }
        const source = axios.CancelToken.source();
        commit("updateCampaignsCancelSource", source);
        commit("updateCampaignsLoading", true);

        try {
            const response = await axios.get("/api/campaigns/", {
                params: state.campaigns_filters,
                cancelToken: source.token,
            });
            commit("updateCampaigns", response.data.results);
            commit("updateCampaignsCount", response.data.count);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit("updateCampaignsLoadingErrors", error.details);
            throw error;
        } finally {
            commit("updateCampaignsLoading", false);
        }
    },

    async saveCampaign({ commit, dispatch }, params) {
        commit("updateCampaignSaving", true);
        commit("updateCampaignSavingErrors", {});
        try {
            const campaign = params.campaign;
            const response = campaign.id
                ? await axios.patch(`/api/campaigns/${campaign.id}/`, campaign)
                : await axios.post("/api/campaigns/", campaign);
            await dispatch("fetchCampaigns");
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit("updateCampaignSavingErrors", error.details);
            throw error;
        } finally {
            commit("updateCampaignSaving", false);
        }
    },

    async deleteCampaign({ dispatch }, params) {
        try {
            await axios.delete(`/api/campaigns/${params.campaign.id}/`);
            await dispatch("fetchCampaigns");
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async archiveCampaign({ dispatch }, params) {
        try {
            await axios.post(`/api/campaigns/${params.campaign.id}/archive/`);
            await dispatch("fetchCampaigns");
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async unarchiveCampaign({ dispatch }, params) {
        try {
            await axios.post(`/api/campaigns/${params.campaign.id}/unarchive/`);
            await dispatch("fetchCampaigns");
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    /**
     * The composer settings are stable for a session (sender identity, quota,
     * variable catalog), so they are fetched once and shared by every screen.
     */
    async fetchSettings({ commit, state }, params) {
        if (state.settings && !params?.force) {
            return state.settings;
        }
        commit("updateSettingsLoading", true);
        try {
            const response = await axios.get("/api/communications/settings/");
            commit("updateSettings", response.data);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateSettingsLoading", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
